import { API_STOCK_OUT_REPORT } from 'utils/constants'
import Api from './axiosInstance'

// The only filter keys the API understands. Anything else it receives is turned
// into a case-insensitive regex filter on a field of that name, so a stray key
// (`sortBy`, `isEditor`, …) silently returns an empty listing. Whitelisting here
// keeps that impossible for both read endpoints.
const FILTER_KEYS = [
  'search',
  'barcode',
  'store',
  'sapStoreCode',
  'dept',
  'section',
  'family',
  'sapDept',
  'sapSection',
  'sapFamily',
  // 'open' = still out of stock, 'closed' = back in stock
  'status',
  // Stock-out range. `from`/`to` are the server's aliases for the same range.
  'stockOutFrom',
  'stockOutTo',
  // Stock-in range - only ever matches entries that have come back in stock.
  'stockInFrom',
  'stockInTo',
] as const

// `extra` carries non-filter params (page/limit) and bypasses the whitelist.
// Empty values are dropped so blanks never reach the server.
const buildQueryString = (
  filters: Record<string, any> = {},
  extra: Record<string, any> = {},
) => {
  const searchParams = new URLSearchParams()

  const append = (key: string, value: any) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value))
    }
  }

  FILTER_KEYS.forEach((key) => append(key, filters[key]))
  Object.entries(extra).forEach(([key, value]) => append(key, value))

  return searchParams.toString()
}

const withQuery = (path: string, queryString: string) =>
  queryString ? `${path}?${queryString}` : path

// Paginated listing. Sorting is fixed server-side (newest stock-out first), so
// there is no sort param to pass.
export const fetchStockOutReport = (
  filters: Record<string, any>,
  pagination: { page: number | string; limit: number | string },
) => {
  return Api.get({
    url: withQuery(
      API_STOCK_OUT_REPORT.STOCK_OUT_REPORT,
      buildQueryString(filters, pagination),
    ),
  })
}

// Queues a CSV export of the filtered rows — asynchronous. A 200 only means the
// job was accepted: a worker mails the requesting user a download link and logs
// the run on the Export Logs screen under module STOCK_OUT_REPORT. Responds 422
// with `errors.msg.runningService` when an export is already in flight (one at a
// time, across all users).
export const exportStockOutReport = (filters: Record<string, any> = {}) => {
  return Api.get({
    url: withQuery(API_STOCK_OUT_REPORT.EXPORT, buildQueryString(filters)),
  })
}
